
import React from 'react';
import { BarChart3, Users, Crown, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMarketAnalysisUsage } from '@/hooks/useMarketAnalysisUsage';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/sidebar';

interface MarketAnalysisUsageWidgetProps {
  className?: string;
}

export const MarketAnalysisUsageWidget: React.FC<MarketAnalysisUsageWidgetProps> = ({
  className = ''
}) => {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  
  const {
    canUse,
    remainingDaily,
    remainingMonthly,
    dailyLimit,
    monthlyLimit,
    dailyUsage,
    monthlyUsage,
    source,
    isLoading
  } = useMarketAnalysisUsage();

  const getSourceConfig = (source: string) => {
    switch (source) {
      case 'affiliate':
        return {
          icon: Users,
          label: 'Affiliate',
          color: 'bg-purple-500/20 text-purple-700 border-purple-500/30',
          description: 'Via affiliate program'
        };
      case 'package':
        return {
          icon: Crown,
          label: 'Premium',
          color: 'bg-amber-500/20 text-amber-700 border-amber-500/30',
          description: 'Premium subscription'
        };
      case 'admin':
        return {
          icon: Zap,
          label: 'Unlimited',
          color: 'bg-emerald-500/20 text-emerald-700 border-emerald-500/30',
          description: 'Admin access'
        };
      default:
        return {
          icon: BarChart3,
          label: 'Free',
          color: 'bg-blue-500/20 text-blue-700 border-blue-500/30',
          description: 'Free tier'
        };
    }
  };

  const sourceConfig = getSourceConfig(source);
  const dailyUsagePercentage = dailyLimit > 0 ? (dailyUsage / dailyLimit) * 100 : 0;
  const monthlyUsagePercentage = monthlyLimit > 0 ? (monthlyUsage / monthlyLimit) * 100 : 0;

  if (isLoading) {
    return (
      <div className={cn("p-3 bg-muted/30 rounded-lg animate-pulse", className)}>
        <div className="h-4 bg-muted rounded mb-2" />
        <div className="h-2 bg-muted rounded" />
      </div>
    );
  }

  // Collapsed state - show compact version with tooltip
  if (isCollapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(
              "p-2 bg-gradient-to-br from-muted/20 to-background/50 rounded-lg border border-border/30 hover:border-border/50 transition-all duration-200",
              !canUse && "border-amber-500/30 bg-amber-500/5",
              className
            )}>
              <div className="flex flex-col items-center gap-1">
                <sourceConfig.icon size={16} className={
                  canUse ? "text-muted-foreground" : "text-amber-600"
                } />
                <div className="text-xs font-medium">
                  {remainingDaily}/{dailyLimit}
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={sourceConfig.color}>
                  {sourceConfig.label}
                </Badge>
              </div>
              
              {/* Daily Usage */}
              <div className="space-y-1">
                <div className="text-sm font-medium">Daily Usage</div>
                <div className="text-xs">
                  <div>Used: {dailyUsage}/{dailyLimit}</div>
                  <div>Remaining: {remainingDaily}</div>
                </div>
              </div>

              {/* Monthly Usage */}
              <div className="space-y-1">
                <div className="text-sm font-medium">Monthly Usage</div>
                <div className="text-xs">
                  <div>Used: {monthlyUsage}/{monthlyLimit}</div>
                  <div>Remaining: {remainingMonthly}</div>
                </div>
              </div>

              {!canUse && (
                <div className="text-xs text-amber-600">
                  Daily limit reached
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded state - show full details
  return (
    <div className={cn(
      "p-3 bg-gradient-to-br from-muted/20 to-background/50 rounded-lg border border-border/30 hover:border-border/50 transition-all duration-200",
      !canUse && "border-amber-500/30 bg-amber-500/5",
      className
    )}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 size={14} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Usage</span>
          </div>
          <Badge variant="outline" className={cn("text-xs", sourceConfig.color)}>
            {sourceConfig.label}
          </Badge>
        </div>

        {/* Daily Usage Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Daily</span>
            <span className="text-xs font-medium">
              {dailyUsage}/{dailyLimit}
            </span>
          </div>
          
          <Progress 
            value={dailyUsagePercentage} 
            className={cn(
              "h-1.5",
              dailyUsagePercentage >= 90 && "bg-red-100",
              dailyUsagePercentage >= 70 && dailyUsagePercentage < 90 && "bg-amber-100"
            )}
          />
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Remaining</span>
            <span className={cn(
              "text-xs font-medium",
              remainingDaily === 0 ? "text-red-600" : 
              remainingDaily <= 2 ? "text-amber-600" : "text-emerald-600"
            )}>
              {remainingDaily}
            </span>
          </div>
        </div>

        {/* Monthly Usage Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Monthly</span>
            <span className="text-xs font-medium">
              {monthlyUsage}/{monthlyLimit}
            </span>
          </div>
          
          <Progress 
            value={monthlyUsagePercentage} 
            className={cn(
              "h-1.5",
              monthlyUsagePercentage >= 90 && "bg-red-100",
              monthlyUsagePercentage >= 70 && monthlyUsagePercentage < 90 && "bg-amber-100"
            )}
          />
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Remaining</span>
            <span className={cn(
              "text-xs font-medium",
              remainingMonthly === 0 ? "text-red-600" : 
              remainingMonthly <= 5 ? "text-amber-600" : "text-emerald-600"
            )}>
              {remainingMonthly}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        {!canUse && (
          <div className="text-xs text-amber-600 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20">
            Daily limit reached
          </div>
        )}
      </div>
    </div>
  );
};
